import { useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb } from "@/components/breadcrumb";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { usePaginatedMatches } from "@/components/use-paginated-matches";
import {
  SINGLE_SCREENING_RESULTS_COLUMNS_META,
  ADV_MEDIA_RESULTS_COLUMNS_META,
  ResultsColumnHeaders,
  ResultsTable,
  useBatchResultsColumnPrefs,
  useBatchAdvMediaColumnPrefs,
  useResultsSort,
  type ResultsColumnId,
} from "@/app/(app)/screening/_components/results-table";
import { TableColumnsMenu } from "@/components/table-columns-menu";
import { MatchDetail } from "@/app/(app)/screening/_components/match-detail";
import { SearchSummary } from "@/components/search-summary";
import type { Batch, BatchRow } from "@/app/(app)/_data/batches";
import { getBatch, getBatchRowMatches } from "@/src/services/batch_service";
import NotFoundPage from "@/src/pages/_errors/NotFoundPage";

type LoadStatus = "loading" | "error" | "notfound" | "ready";

export function BatchSearchMatches({ batchId, searchId }: { batchId: string; searchId: string }) {
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [batch, setBatch] = useState<Batch | null>(null);
  const [row, setRow] = useState<BatchRow | null>(null);
  const [sort, setSort] = useResultsSort();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Bumped by the inline error card's "Retry" button.
  const [retryNonce, setRetryNonce] = useState(0);

  // A row is entirely Adverse Media or entirely another data source, never
  // mixed (row.dataSource, already computed by adaptDetailRow) — so the
  // column set is picked once per row, not per match.
  const isAdvMedia = row?.dataSource.includes("ADV-MEDIA") ?? false;
  const watchlistPrefs = useBatchResultsColumnPrefs();
  const advMediaPrefs = useBatchAdvMediaColumnPrefs();
  const activePrefs = isAdvMedia ? advMediaPrefs : watchlistPrefs;

  const load = useCallback(async () => {
    setStatus("loading");
    const res = await getBatch(batchId);
    if (!res.ok) {
      setStatus(res.error.code === "not_found" ? "notfound" : "error");
      return;
    }
    const found = res.value.rows.find((r) => r.id === searchId);
    if (!found) {
      setStatus("notfound");
      return;
    }
    setBatch(res.value);
    setRow(found);
    setStatus("ready");
  }, [batchId, searchId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only ever invoked once `row` is set — `usePaginatedMatches` receives
  // `null` instead of this callback until then (see `row ? fetchPage : null`
  // below), same non-null-assertion style the JSX below already uses for `row`.
  const fetchPage = useCallback((p: number) => getBatchRowMatches(row!, p), [row]);
  const resetKey = useMemo(() => ({ rowId: row?.id, retryNonce }), [row?.id, retryNonce]);
  const result = usePaginatedMatches(
    row ? fetchPage : null,
    resetKey,
    page,
    activePrefs.rowsPerPage,
  );
  const matches = result.matches;

  useEffect(() => setPage(1), [row?.id, activePrefs.rowsPerPage]);

  useEffect(() => {
    if (selectedId && matches.some((m) => m.id === selectedId)) return;
    setSelectedId(matches.length > 0 ? matches[0].id : null);
  }, [matches, selectedId]);

  const selectedIndex = selectedId ? matches.findIndex((m) => m.id === selectedId) : -1;
  const selected = selectedIndex >= 0 ? matches[selectedIndex] : null;

  function step(delta: number) {
    if (selectedIndex < 0) return;
    const next = selectedIndex + delta;
    if (next < 0 || next >= matches.length) return;
    setSelectedId(matches[next].id);
  }

  if (status === "notfound") return <NotFoundPage />;

  const pageCount = Math.max(1, Math.ceil(result.count / activePrefs.rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const individuals = matches.filter((m) => m.type === "Individual").length;
  const organizations = matches.filter((m) => m.type === "Organization").length;
  const others = matches.filter(
    (m) => m.type === "Vessel" || m.type === "Aircraft" || m.type === "Country",
  ).length;

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-white px-4 pb-3 pt-4 sm:px-8">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          backHref={`/batch/${batchId}`}
          segments={[
            { label: "Batch Screening", href: "/batch" },
            { label: batch?.filename ?? "…", href: `/batch/${batchId}` },
            { label: row?.name ?? "…", current: true },
          ]}
        />

        {status === "error" ? (
          <BatchMatchesError onRetry={load} />
        ) : status === "loading" ? (
          <MatchesSkeleton />
        ) : (
          <>
            <SearchSummary
              name={row!.name}
              entityType={row!.entityType}
              country={row!.country}
              dob={row!.dateOfBirth}
              identifier={row!.identifier ?? row!.externalId}
              minScore={row!.minScore}
              dataSource={row!.dataSource}
            />

            {result.error ? (
              <BatchMatchesError onRetry={() => setRetryNonce((n) => n + 1)} />
            ) : (
              <div className="overflow-hidden rounded-[4px] border border-grey-200 bg-white shadow-flat">
                {result.isLoading ? (
                  <RowsSkeleton />
                ) : matches.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between gap-2 px-4 py-3">
                      <h2 className="text-h4 text-black">
                        {isAdvMedia ? "Articles" : "Matches"} ({result.count})
                      </h2>
                      <TableColumnsMenu
                        columns={
                          isAdvMedia
                            ? ADV_MEDIA_RESULTS_COLUMNS_META
                            : SINGLE_SCREENING_RESULTS_COLUMNS_META
                        }
                        hidden={activePrefs.hidden}
                        onToggle={(id) => activePrefs.toggleHidden(id as ResultsColumnId)}
                        onReset={activePrefs.reset}
                      />
                    </div>
                    <div className="max-h-[420px] overflow-auto">
                      <div className="min-w-[1040px]">
                        <div className="sticky top-0 z-10">
                          <ResultsColumnHeaders
                            sort={sort}
                            onSort={setSort}
                            order={activePrefs.order}
                            hidden={activePrefs.hidden}
                            onReorder={activePrefs.setOrder}
                            widths={activePrefs.widths}
                            onResizeColumn={activePrefs.setWidth}
                          />
                        </div>
                        <ResultsTable
                          matches={matches}
                          sort={sort}
                          order={activePrefs.order}
                          hidden={activePrefs.hidden}
                          onSelect={setSelectedId}
                          selectedId={selectedId}
                          widths={activePrefs.widths}
                          page={safePage}
                          rowsPerPage={activePrefs.rowsPerPage}
                        />
                      </div>
                    </div>
                    <PaginationFooter
                      total={result.count}
                      totalLabel={isAdvMedia ? "articles" : "matches"}
                      {...(isAdvMedia ? {} : { individuals, organizations, others })}
                      page={safePage}
                      pageCount={pageCount}
                      onPageChange={setPage}
                      rowsPerPage={activePrefs.rowsPerPage}
                      onRowsPerPageChange={(n) => {
                        activePrefs.setRowsPerPage(n);
                        setPage(1);
                      }}
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 px-4 py-12">
                    <h3 className="text-h4 text-black">No matches</h3>
                    <p className="text-small text-grey-900">This record didn’t match any names.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {status === "ready" && selected && (
        <div className="mt-3">
          <MatchDetail
            embedded
            showEntityMeta
            match={selected}
            position={{ current: selectedIndex + 1, total: matches.length }}
            onPrev={selectedIndex > 0 ? () => step(-1) : undefined}
            onNext={
              selectedIndex >= 0 && selectedIndex < matches.length - 1 ? () => step(1) : undefined
            }
          />
        </div>
      )}
    </div>
  );
}

function MatchesSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <div className="h-16 w-full animate-pulse rounded-[4px] bg-grey-100" />
      <div className="flex flex-col gap-px overflow-hidden rounded-[4px] border border-grey-200">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 w-full animate-pulse bg-grey-50" />
        ))}
      </div>
    </div>
  );
}

function RowsSkeleton() {
  return (
    <div role="status" aria-label="Loading matches" className="flex flex-col">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex h-12 animate-pulse items-center gap-3 border-b border-grey-100 px-3"
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <div className="h-4 flex-1 max-w-[260px] rounded-[4px] bg-grey-200" />
          <div className="h-4 w-32 rounded-[4px] bg-grey-200" />
          <div className="h-4 w-20 rounded-[4px] bg-grey-200" />
          <div className="h-4 w-24 rounded-[4px] bg-grey-200" />
        </div>
      ))}
    </div>
  );
}

function BatchMatchesError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[4px] border border-grey-200 bg-white px-4 py-12 text-center shadow-flat">
      <div className="flex size-9 items-center justify-center rounded-[4px] bg-red-100 text-red-600">
        <Icon name="error" size={20} />
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-body font-medium text-black">Couldn’t load matches</p>
        <p className="text-small text-grey-900">Something went wrong fetching this record.</p>
      </div>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}
